import React from 'react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
// @ts-ignore
import DragSortableList from 'react-drag-sortable';
import AddHeader from '../../../../../components/layout/header/add';
import useAisleDetails from './hooks';
import Forms from '../../../../../components/molecules/forms';
import {
  Delete, LineStroke, Print, SixDot, Upload,
} from '../../../../../components/atoms/icons';
import { numberWithZeroInitFormat } from '../../../../../components/atoms/func';
import { COLOR_PRIMARY, successUpdateMsg } from '../../../../../components/atoms/const';
import DataListHandler from '../../../../../components/layout/body/data-list-handler';
import Modal from '../../../../../components/organisms/modals';
import Button from '../../../../../components/molecules/buttons';

function AisleLevelDetails() {
  const {
    aisle, warehouseName, modalDelete, modalAddProduct,
    productId, afterSubmit, modalClearAllItems, handleMoveItem, searchData,
  } = useAisleDetails();

  const renderModalBodyDelete = React.useMemo(() => {
    if (!modalDelete.result) return 'Are you sure to delete this product?';
    return successUpdateMsg.SUCCESS_DELETE_PRODUCT_MSG;
  }, [modalDelete.result]);

  const renderModalBodyClearItems = React.useMemo(() => {
    if (!modalClearAllItems.result) return 'Are you sure to delete all products?';
    return successUpdateMsg.SUCCESS_DELETE_ALL_PRODUCT_LEVEL;
  }, [modalClearAllItems.result]);

  const renderModalBodyAddProduct = () => {
    if (!modalAddProduct.result) {
      return (
        <Forms.ReactSelect.AsyncMode
          id="productId"
          className="mb-4"
          placeholder="Search for product"
          optionsFetcher={productId.optionsFetcher}
          inputValue={productId.search.current}
          onChangeInput={(e) => productId.search.setCurrent(e)}
          onChangeSelected={(e) => {
            if (e) productId.setCurrent(e);
          }}
          selectedValue={productId.current}
        />
      );
    }
    return successUpdateMsg.SUCCESS_ADD_PRODUCT_LEVEL;
  };

  const renderHeaderTopRightItem = () => (
    <div className="d-flex gap-3">
      <Print width={30} />
      <Upload />
    </div>
  );

  const renderAddProduct = (i: number, condition: 'UP' | 'DOWN', isIconAdd: boolean = true) => (
    <Modal.Default
      cancelLabel="Cancel"
      submitLabel="Add Product"
      title="Add New Product"
      body={renderModalBodyAddProduct()}
      result={modalAddProduct.result}
      isLoading={modalAddProduct.load}
      onSubmit={() => modalAddProduct.submit(i, condition)}
      onAfterResult={afterSubmit}
      isConfirmDisabled={productId.current === null}
      onInit={() => productId.setCurrent(null)}
    >
      {isIconAdd
        ? <AddCircleIcon sx={{ fontSize: 30, color: COLOR_PRIMARY, marginTop: '-2px' }} />
        : <Button.Default label="Add Item" isButton={false} />}
    </Modal.Default>
  );

  const renderItemAisle = (data: typeof aisle.data[0], i: number) => (
    <div key={data.id}>
      <div className="d-flex px-4 px-xl-5 align-items-center fz-14">
        <div className="d-flex align-items-center">
          <button type="button" className="bg-transparent border-0" style={{ marginTop: '-4px' }}>
            <SixDot />
          </button>
          <div className="fw-bold ms-2 ms-xl-3 me-4">{numberWithZeroInitFormat(i + 1, 3)}</div>
        </div>
        <div className="flex-grow-1 d-flex align-items-center justify-content-between flex-wrap">
          <div>{`${data.product.name} - ${data.product.unit ?? 0} count`}</div>
          <div className="d-flex align-items-center">
            <Modal.Default
              cancelLabel="No"
              submitLabel="Yes"
              title="Delete Product"
              body={renderModalBodyDelete}
              onAfterResult={afterSubmit}
              onSubmit={() => modalDelete.submit(data.id)}
              result={modalDelete.result}
              isLoading={modalDelete.load}
            >
              <div className="me-2" style={{ marginBottom: 4 }}><Delete width={18} /></div>
            </Modal.Default>
            {renderAddProduct(i, 'UP')}
            <div className="fz-20 color-secondary me-1" style={{ marginTop: '-6px' }}>&#8593;</div>
            {renderAddProduct(i, 'DOWN')}
            <div className="fz-20 color-secondary" style={{ marginTop: '-6px' }}>&#8595;</div>
          </div>
        </div>
      </div>
      <LineStroke height={3} marginVertical={3} />
    </div>
  );

  return (
    <div>
      <AddHeader
        primary={{ route: '/warehouse', title: 'Warehouse' }}
        title={`${warehouseName} - Level ${aisle.level}`}
        topRightItem={renderHeaderTopRightItem()}
      />
      <div className="px-4 px-xl-5 mb-xl-5 mb-4">
        <div className="row">
          <div className="col-xl-9 mb-4">
            <div style={{ height: '42px' }}>
              <Forms.Search
                value={searchData.current}
                onChange={(e) => searchData.setCurrent(e.target.value)}
              />
            </div>
          </div>
          <div className="col-xl-3">
            <div className="row">
              <div className="col-sm-6 mb-4">
                <Modal.Default
                  cancelLabel="Cancel"
                  submitLabel="Delete"
                  title="Delete All Products"
                  body={renderModalBodyClearItems}
                  result={modalClearAllItems.result}
                  isLoading={modalClearAllItems.load}
                  onSubmit={modalClearAllItems.submit}
                  onAfterResult={afterSubmit}
                >
                  <Button.Default
                    label="Clear Items"
                    isButton={false}
                    outline
                  />
                </Modal.Default>
              </div>
              <div className="col-sm-6 mb-4">
                {renderAddProduct(0, 'UP', false)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DataListHandler isData={aisle.data?.length > 0} isLoading={aisle.load}>
        <div>
          <DragSortableList
            items={aisle.data?.map((data, i) => ({
              content: renderItemAisle(data, i),
              id: data.id.toString(),
            }))}
            onSort={(list: any, e: any) => {
              const find = list.find((x: any) => x.id.toString() === e.currentTarget.getAttribute('data-id'));
              if (find) handleMoveItem(list.indexOf(find), e.currentTarget.getAttribute('data-id'));
            }}
            type="vertical"
          />
        </div>
      </DataListHandler>
    </div>
  );
}

export default AisleLevelDetails;
